import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import Portal from '../portal/portal.js';
import PendoDivider from '../divider/pendo-divider.vue.js';
import PendoDrawerClose from './pendo-drawer-close.vue.js';
import FocusTrap from '../../utils/focus-trap.js';
import { getTarget, setStyles, getMargin } from '../../utils/dom.js';
import { capitalize } from '../../utils/utils.js';
import { disableBodyScroll, enableBodyScroll } from '../../utils/body-scroll-lock.js';

var script = {
  name: 'PendoDrawer',
  components: {
    PendoDivider,
    PendoDrawerClose,
    FocusTrap,
    Portal
  },
  provide() {
    return {
      $drawer: this
    };
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    className: {
      type: [String, Array],
      default: null
    },
    size: {
      type: String,
      default: 'medium'
    },
    title: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    showClose: {
      type: Boolean,
      default: true
    },
    closeOnMaskClick: {
      type: Boolean,
      default: true
    },
    mask: {
      type: Boolean,
      default: true
    },
    placement: {
      type: String,
      default() {
        if (this.size === 'fullscreen') {
          return 'bottom';
        }
        return 'right';
      },
      validator: placement => ['top', 'bottom', 'right', 'left'].includes(placement)
    },
    zIndex: {
      type: Number,
      default: 502
    },
    push: {
      type: [String, Element, Boolean],
      default: null
    },
    duration: {
      type: Number,
      default: 220
    },
    appendTo: {
      type: [String, Element, Boolean],
      default: 'body'
    },
    focusTrapConfig: {
      type: Object,
      default: () => ({})
    },
    positionStrategy: {
      type: String,
      default: 'fixed',
      validator: strategy => ['fixed', 'absolute'].includes(strategy)
    }
  },
  data() {
    return {
      id: `pendo-drawer-${this._uid}`,
      isFocusTrapActive: false,
      isPanelVisible: false,
      isMaskVisible: false,
      isVisible: this.visible,
      oldPushElMargin: 0,
      oldPushElPlacement: this.placement
    };
  },
  computed: {
    topLevelDrawerElementWidth() {
      if (this.positionStrategy === 'absolute' && this.showMask && this.isVisible) {
        return '100%';
      }
      return 'auto';
    },
    titleId() {
      return `${this.id}-title`;
    },
    oppositePlacement() {
      return {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left'
      }[this.placement];
    },
    drawerSize() {
      if (['small', 'medium', 'large', 'fullscreen'].includes(this.size)) {
        return {
          small: '400px',
          medium: '500px',
          large: '550px',
          fullscreen: '100'
        }[this.size];
      }
      return this.size;
    },
    panelStyles() {
      const styles = {
        transitionDuration: `${this.duration}ms`,
        position: this.positionStrategy
      };
      if (this.push) {
        styles[`border-${this.oppositePlacement}`] = '1px solid #DADCE5';
        styles.boxShadow = 'none';
      }
      if (['top', 'bottom'].includes(this.placement)) {
        return _objectSpread2(_objectSpread2({}, styles), {}, {
          height: this.size === 'fullscreen' ? '100vh' : this.drawerSize
        });
      }
      return _objectSpread2(_objectSpread2({}, styles), {}, {
        width: this.size === 'fullscreen' ? '100vw' : this.drawerSize
      });
    },
    showMask() {
      if (this.size === 'fullscreen') {
        return false;
      }
      return this.push ? this.$options.propsData.mask : this.mask;
    },
    pushEl() {
      if (this.push) {
        return getTarget(this.push);
      }
      return false;
    },
    hasLabel() {
      return this.label || this.$slots.label || this.$scopedSlots.label;
    },
    gridTemplateRows() {
      const footer = this.$slots.footer || this.$scopedSlots.footer;
      if (this.hasLabel && footer) {
        return '[before-header] minmax(58px, max-content) [header] 68px [body] auto [footer] minmax(68px, max-content)';
      }
      if (this.hasLabel) {
        return '[before-header] minmax(58px, max-content) [header] 68px [body] 1fr';
      }
      if (footer) {
        return '[header] 68px [body] auto [footer] minmax(68px, max-content)';
      }
      return '[header] 68px [body] 1fr';
    },
    gridTemplateAreas() {
      const aside = this.$slots.aside || this.$scopedSlots.aside;
      const beforeHeader = this.hasLabel ? "'before-header before-header' " : '';
      const body = aside ? 'aside body' : 'body body';
      return `${beforeHeader}'header header' '${body}' 'footer footer'`;
    },
    gridStyles() {
      const aside = this.$slots.aside || this.$scopedSlots.aside;
      let gridTemplateColumns;
      if (aside) {
        gridTemplateColumns = '25% 1fr';
      }
      return {
        gridTemplateAreas: this.gridTemplateAreas,
        gridTemplateColumns,
        gridTemplateRows: this.gridTemplateRows,
        gridColumnGap: aside ? '32px' : 0
      };
    }
  },
  watch: {
    push(newValue, oldValue) {
      if (!newValue && oldValue) {
        setStyles(getTarget(oldValue), {
          transitionDelay: '0ms',
          [`margin-${this.oldPushElPlacement}`]: this.oldPushElMargin
        });
        this.oldPushElPlacement = this.placement;
      }
    },
    visible: {
      handler() {
        if (this.visible) {
          requestAnimationFrame(() => {
            this.isVisible = true;
            this.isPanelVisible = true;
            this.isMaskVisible = true;
          });
        } else if (this.isVisible) {
          this.close();
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.beforeLeave();
  },
  methods: {
    onEscapeKeydown() {
      this.close();
    },
    close() {
      let stopClose = false;
      const stop = () => {
        stopClose = true;
      };
      this.$emit('before-close', {
        stop
      });
      if (!stopClose) {
        this.isPanelVisible = false;
        this.isMaskVisible = false;
        this.$emit('close');
        setTimeout(() => {
          this.$emit('update:visible', false);
          this.$emit('closed');
          this.isVisible = false;
        }, this.duration + 150);
      }
    },
    onMaskClick() {
      if (this.mask && this.closeOnMaskClick) {
        this.close();
      }
    },
    beforeEnter() {
      if (this.pushEl) {
        const styles = {
          willChange: `margin-${this.placement}`,
          transitionDuration: `${this.duration}ms`,
          transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
          transitionDelay: '70ms'
        };
        let movement = this.drawerSize;
        const margins = getMargin(this.pushEl);
        const existingMargin = margins[`margin${capitalize(this.placement)}`];
        if (existingMargin) {
          this.oldPushElMargin = existingMargin;
          movement = `calc(${existingMargin} + ${this.drawerSize})`;
        }
        styles[`margin-${this.placement}`] = movement;
        setStyles(this.pushEl, styles);
      }
      if (this.size === 'fullscreen') {
        disableBodyScroll(this.id);
      }
      this.$emit('open');
    },
    afterEnter() {
      this.isFocusTrapActive = true;
      this.$emit('opened');
    },
    beforeLeave() {
      if (this.pushEl) {
        const styles = {
          transitionDelay: '0ms'
        };
        if (this.oldPushElMargin) {
          styles[`margin-${this.placement}`] = this.oldPushElMargin;
        } else {
          this.pushEl.style.removeProperty(`margin-${this.placement}`);
        }
        setStyles(this.pushEl, styles);
      }
      if (this.size === 'fullscreen') {
        enableBodyScroll(this.id);
      }
    },
    afterLeave() {
      requestAnimationFrame(() => {
        enableBodyScroll(this.id);
        this.isFocusTrapActive = false;
      });
      if (this.pushEl) {
        this.pushEl.style.removeProperty(`border-${this.placement}`);
        this.pushEl.style.removeProperty('transition-duration');
        this.pushEl.style.removeProperty('transition-delay');
        this.pushEl.style.removeProperty('transition-timing-function');
      }
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-drawer.vue2.js.map
